import { UserGroupEnum, UserRoleEnum } from '@kurotetsu/share/constants';
import type { User } from '@kurotetsu/share/context/AuthContext';

type KktMenuRoleConfig = {
  userRole: readonly UserRoleEnum[];
  userGroup: readonly UserGroupEnum[];
};

type MenuRoleConfig = {
  releasePhase?: number;
  allowedEmails?: readonly string[];
  isKKT?: KktMenuRoleConfig | boolean;
  isAgency?: boolean;
  isSales?: boolean;
  isIndividual?: boolean;
};

const kktRoles = [UserRoleEnum.Admin, UserRoleEnum.Applicant] as const;

export const menuRoleConfig = {
  dashboard: {
    releasePhase: 1,
    isKKT: {
      userRole: kktRoles,
      userGroup: [
        UserGroupEnum.SALES_DEPARTMENT,
        UserGroupEnum.TRANSPORTATION_STATION_STAFF
      ]
    },
    isAgency: true,
    isSales: true,
    isIndividual: true
  },
  groupManager: {
    isKKT: {
      userRole: kktRoles,
      userGroup: [
        UserGroupEnum.MERCHANDISE_MANAGEMENT_GROUP,
        UserGroupEnum.SALES_DEPARTMENT,
        UserGroupEnum.TRANSPORTATION_STATION_STAFF
      ]
    },
    isAgency: true,
    isSales: true,
    isIndividual: true
  },
  groupManager_notificationList: {
    isKKT: {
      userRole: kktRoles,
      userGroup: [UserGroupEnum.SALES_DEPARTMENT]
    },
    isAgency: true,
    isSales: true,
    isIndividual: true
  },
  groupManager_bookingSearch: {
    isKKT: {
      userRole: kktRoles,
      userGroup: [
        UserGroupEnum.SALES_DEPARTMENT,
        UserGroupEnum.TRANSPORTATION_STATION_STAFF
      ]
    },
    isAgency: true,
    isSales: true,
    isIndividual: true
  },
  groupManager_periodSelect: {
    isKKT: {
      userRole: kktRoles,
      userGroup: [
        UserGroupEnum.SALES_DEPARTMENT,
        UserGroupEnum.TRANSPORTATION_STATION_STAFF
      ]
    },
    isAgency: true,
    isSales: true,
    isIndividual: true
  },
  groupManager_bulkOperation: {
    isKKT: {
      userRole: kktRoles,
      userGroup: [UserGroupEnum.SALES_DEPARTMENT]
    }
  },
  groupManager_tourBookingList: {
    isKKT: {
      userRole: kktRoles,
      userGroup: [UserGroupEnum.SALES_DEPARTMENT]
    },
    isAgency: true,
    isSales: true,
    isIndividual: false
  },
  groupManager_restaurantBookingList: {
    isKKT: {
      userRole: kktRoles,
      userGroup: [
        UserGroupEnum.MERCHANDISE_MANAGEMENT_GROUP,
        UserGroupEnum.SALES_DEPARTMENT
      ]
    },
    isAgency: true,
    isSales: true,
    isIndividual: true
  },
  groupManager_report: {
    isKKT: {
      userRole: kktRoles,
      userGroup: [UserGroupEnum.SALES_DEPARTMENT]
    }
  },
  groupSalesManager: {
    isKKT: {
      userRole: kktRoles,
      userGroup: [
        UserGroupEnum.MERCHANDISE_MANAGEMENT_GROUP,
        UserGroupEnum.SALES_DEPARTMENT
      ]
    },
    isAgency: true,
    isSales: true,
    isIndividual: true
  },
  groupSalesManager_salesManager: {
    isKKT: {
      userRole: kktRoles,
      userGroup: [
        UserGroupEnum.MERCHANDISE_MANAGEMENT_GROUP,
        UserGroupEnum.SALES_DEPARTMENT
      ]
    },
    isAgency: true,
    isSales: true,
    isIndividual: true
  },
  serviceManager: {
    releasePhase: 1,
    isKKT: {
      userRole: kktRoles,
      userGroup: [
        UserGroupEnum.SALES_DEPARTMENT,
        UserGroupEnum.TRANSPORTATION_STATION_STAFF
      ]
    }
  },
  serviceManager_trainAssignment: {
    releasePhase: 1,
    isKKT: {
      userRole: kktRoles,
      userGroup: [UserGroupEnum.SALES_DEPARTMENT]
    }
  },
  serviceManager_trainAssignmentPublic: {
    releasePhase: 1,
    isKKT: {
      userRole: kktRoles,
      userGroup: [UserGroupEnum.SALES_DEPARTMENT]
    }
  },
  serviceManager_specialSeatAdjustmentSetting: {
    releasePhase: 1,
    isKKT: {
      userRole: kktRoles,
      userGroup: [
        UserGroupEnum.SALES_DEPARTMENT,
        UserGroupEnum.TRANSPORTATION_STATION_STAFF
      ]
    }
  },
  serviceManager_suspension: {
    releasePhase: 1,
    isKKT: {
      userRole: kktRoles,
      userGroup: [UserGroupEnum.SALES_DEPARTMENT]
    }
  },
  accountManager: {
    releasePhase: 1,
    isKKT: {
      userRole: kktRoles,
      userGroup: Object.values(UserGroupEnum)
    },
    isAgency: true,
    isSales: true,
    isIndividual: true
  },
  accountManager_operatorUserManagement: {
    releasePhase: 1,
    isKKT: {
      userRole: [UserRoleEnum.Admin],
      userGroup: [UserGroupEnum.SALES_DEPARTMENT]
    },
    isAgency: false,
    isSales: false,
    isIndividual: false
  },
  accountManager_initialDataBulkRegistration: {
    releasePhase: 1,
    allowedEmails: [
      'snowinday8283@yahoo.co.jp',
      'n-imai@a-zs.co.jp',
      'k-senonn@a-zs.co.jp',
      'yoko.murakami@linktivity.co.jp'
    ],
    isKKT: {
      userRole: [UserRoleEnum.Admin],
      userGroup: [UserGroupEnum.SALES_DEPARTMENT]
    },
    isAgency: false,
    isSales: false,
    isIndividual: false
  },
  accountManager_agencyList: {
    releasePhase: 1,
    isKKT: {
      userRole: kktRoles,
      userGroup: [UserGroupEnum.SALES_DEPARTMENT]
    },
    isAgency: false,
    isSales: false,
    isIndividual: false
  },
  accountManager_agencyInfo: {
    releasePhase: 1,
    isKKT: false,
    isAgency: true,
    isSales: true,
    isIndividual: false
  },
  accountManager_individualGroupList: {
    releasePhase: 1,
    isKKT: {
      userRole: kktRoles,
      userGroup: [UserGroupEnum.SALES_DEPARTMENT]
    },
    isAgency: false,
    isSales: false,
    isIndividual: false
  },
  accountManager_individualGroupManagement: {
    releasePhase: 1,
    isKKT: false,
    isAgency: false,
    isSales: false,
    isIndividual: true
  },
  accountManager_passwordUpdate: {
    releasePhase: 1,
    isKKT: {
      userRole: kktRoles,
      userGroup: Object.values(UserGroupEnum)
    },
    isAgency: true,
    isSales: true,
    isIndividual: true
  },
  serviceInventoryManager: {
    releasePhase: 1,
    isKKT: {
      userRole: kktRoles,
      userGroup: [
        UserGroupEnum.SALES_DEPARTMENT,
        UserGroupEnum.TRANSPORTATION_STATION_STAFF
      ]
    }
  },
  serviceInventoryManager_trainFormation: {
    releasePhase: 1,
    isKKT: {
      userRole: kktRoles,
      userGroup: [UserGroupEnum.SALES_DEPARTMENT]
    }
  },
  serviceInventoryManager_stationStopPatternSetting: {
    releasePhase: 1,
    isKKT: {
      userRole: kktRoles,
      userGroup: [UserGroupEnum.SALES_DEPARTMENT]
    }
  },
  serviceInventoryManager_trainSchedule: {
    releasePhase: 1,
    isKKT: {
      userRole: kktRoles,
      userGroup: [
        UserGroupEnum.SALES_DEPARTMENT,
        UserGroupEnum.TRANSPORTATION_STATION_STAFF
      ]
    }
  },
  serviceInventoryManager_calendar: {
    releasePhase: 1,
    isKKT: {
      userRole: kktRoles,
      userGroup: [UserGroupEnum.SALES_DEPARTMENT]
    }
  },
  masterManager: {
    releasePhase: 1,
    isKKT: {
      userRole: kktRoles,
      userGroup: [
        UserGroupEnum.MERCHANDISE_MANAGEMENT_GROUP,
        UserGroupEnum.SALES_DEPARTMENT
      ]
    }
  },
  masterManager_operatorBasicInfo: {
    isKKT: {
      userRole: kktRoles,
      userGroup: [UserGroupEnum.SALES_DEPARTMENT]
    }
  },
  masterManager_groupFareSetting: {
    isKKT: {
      userRole: kktRoles,
      userGroup: [UserGroupEnum.SALES_DEPARTMENT]
    }
  },
  masterManager_pricingPlans: {
    releasePhase: 1,
    isKKT: {
      userRole: kktRoles,
      userGroup: [UserGroupEnum.SALES_DEPARTMENT]
    }
  },
  masterManager_groupSchedule: {
    isKKT: {
      userRole: kktRoles,
      userGroup: [UserGroupEnum.SALES_DEPARTMENT]
    }
  },
  masterManager_restaurant: {
    isKKT: {
      userRole: kktRoles,
      userGroup: [UserGroupEnum.MERCHANDISE_MANAGEMENT_GROUP]
    }
  },
  masterManager_meal: {
    isKKT: {
      userRole: kktRoles,
      userGroup: [UserGroupEnum.MERCHANDISE_MANAGEMENT_GROUP]
    }
  },
  stationService: {
    isKKT: {
      userRole: kktRoles,
      userGroup: [
        UserGroupEnum.SALES_DEPARTMENT,
        UserGroupEnum.TRANSPORTATION_STATION_STAFF,
        UserGroupEnum.KURONAGI_STATION_ASSISTANT
      ]
    }
  },
  stationService_seatSearch: {
    isKKT: {
      userRole: kktRoles,
      userGroup: [
        UserGroupEnum.SALES_DEPARTMENT,
        UserGroupEnum.TRANSPORTATION_STATION_STAFF
      ]
    }
  },
  // 個人予約一覧/検索（eChg01）
  stationService_bookingSearch: {
    isKKT: {
      userRole: kktRoles,
      userGroup: [
        UserGroupEnum.SALES_DEPARTMENT,
        UserGroupEnum.TRANSPORTATION_STATION_STAFF
      ]
    }
  },
  // 未乗車席開放（eCxl03）
  stationService_unriddenSeatRelease: {
    isKKT: {
      userRole: kktRoles,
      userGroup: [
        UserGroupEnum.SALES_DEPARTMENT,
        UserGroupEnum.TRANSPORTATION_STATION_STAFF
      ]
    }
  },
  // 入退場集計（eRpt02）
  stationService_entryExitSummary: {
    isKKT: {
      userRole: kktRoles,
      userGroup: [
        UserGroupEnum.SALES_DEPARTMENT,
        UserGroupEnum.TRANSPORTATION_STATION_STAFF
      ]
    }
  },
  // 改札機設定（eUse01）
  stationService_gateSettings: {
    isKKT: {
      userRole: kktRoles,
      userGroup: [
        UserGroupEnum.SALES_DEPARTMENT,
        UserGroupEnum.TRANSPORTATION_STATION_STAFF
      ]
    }
  },
  // サイネージ設定（eUse02）
  stationService_signageSettings: {
    isKKT: {
      userRole: kktRoles,
      userGroup: [
        UserGroupEnum.SALES_DEPARTMENT,
        UserGroupEnum.TRANSPORTATION_STATION_STAFF
      ]
    }
  },
  // 機器メンテナンス（eMnt06）
  stationService_deviceMaintenance: {
    isKKT: {
      userRole: kktRoles,
      userGroup: [
        UserGroupEnum.SALES_DEPARTMENT,
        UserGroupEnum.TRANSPORTATION_STATION_STAFF
      ]
    }
  },
  // 黒薙駅補助席検索（eChg01）
  stationService_kuronagiSeatSearch: {
    isKKT: {
      userRole: kktRoles,
      userGroup: [
        UserGroupEnum.SALES_DEPARTMENT,
        UserGroupEnum.TRANSPORTATION_STATION_STAFF,
        UserGroupEnum.KURONAGI_STATION_ASSISTANT
      ]
    }
  },
  // フリープラン
  stationService_freePlan: {
    isKKT: {
      userRole: kktRoles,
      userGroup: [
        UserGroupEnum.SALES_DEPARTMENT,
        UserGroupEnum.TRANSPORTATION_STATION_STAFF
      ]
    }
  },
  stationService_couponTicket: {
    isKKT: {
      userRole: kktRoles,
      userGroup: [
        UserGroupEnum.SALES_DEPARTMENT,
        UserGroupEnum.TRANSPORTATION_STATION_STAFF
      ]
    }
  },
  // 帳票データ出力（eRpt01）
  stationService_report: {
    isKKT: {
      userRole: kktRoles,
      userGroup: [
        UserGroupEnum.SALES_DEPARTMENT,
        UserGroupEnum.TRANSPORTATION_STATION_STAFF
      ]
    }
  }
} as const satisfies Record<string, MenuRoleConfig>;

export type MenuName = keyof typeof menuRoleConfig;

const getCurrentReleasePhase = (): number | undefined => {
  const value = import.meta.env.VITE_RELEASE_PHASE?.trim();
  if (!value) return undefined;

  const phase = Number(value);
  return Number.isInteger(phase) && phase >= 1 ? phase : 0;
};

export const isMenuReleased = (
  menuReleasePhase: number = 2,
  currentReleasePhase: number | undefined = getCurrentReleasePhase()
): boolean =>
  currentReleasePhase === undefined || menuReleasePhase <= currentReleasePhase;

export const isDashboardContentReleased = (
  currentReleasePhase: number | undefined = getCurrentReleasePhase()
): boolean => isMenuReleased(2, currentReleasePhase);

export const canViewMenu = (menuName: MenuName, user: User): boolean => {
  const config: MenuRoleConfig = menuRoleConfig[menuName];
  if (!config) return false;
  if (!isMenuReleased(config.releasePhase)) return false;
  if (
    config.allowedEmails &&
    !config.allowedEmails.includes(user.email.trim().toLowerCase())
  ) {
    return false;
  }

  if (user.isKKT) {
    if (typeof config.isKKT === 'object') {
      return (
        config.isKKT?.userRole.includes(user.role!) === true &&
        config.isKKT.userGroup.includes(user.userGroup as UserGroupEnum)
      );
    } else {
      return false;
    }
  } else if (user.isAgency) {
    return config.isAgency === true;
  } else if (user.isSales) {
    return config.isSales === true;
  } else if (user.isIndividual) return config.isIndividual === true;

  return false;
};
